"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { DayPicker, type DayPickerProps } from "react-day-picker";
import { setMonth, setYear } from "date-fns";
import { cn } from "@/lib/utils";

export type CalendarProps = DayPickerProps & {
  showMonthYearPicker?: boolean;
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  showMonthYearPicker = true,
  ...props
}: CalendarProps) {
  const [monthPickerOpen, setMonthPickerOpen] = React.useState(false);
  const [yearPickerOpen, setYearPickerOpen] = React.useState(false);
  
  // Get the initial month from selected date, defaultMonth, or current date
  const getInitialMonth = () => {
    if (props.selected && props.selected instanceof Date) {
      return props.selected;
    }
    return props.defaultMonth || new Date();
  };
  
  const [displayMonth, setDisplayMonth] = React.useState(getInitialMonth);
  
  // Sync displayMonth when selected date changes
  React.useEffect(() => {
    if (props.selected && props.selected instanceof Date) {
      setDisplayMonth(props.selected);
    }
  }, [props.selected]);

  // Generate year options (current year + 10 years into future only)
  const currentYear = new Date().getFullYear();
  const years = React.useMemo(() => {
    const yearList = [];
    for (let y = currentYear; y <= currentYear + 10; y++) {
      yearList.push(y);
    }
    return yearList;
  }, [currentYear]);

  const handleMonthSelect = (monthIndex: number) => {
    const newDate = setMonth(displayMonth, monthIndex);
    setDisplayMonth(newDate);
    setMonthPickerOpen(false);
  };

  const handleYearSelect = (year: number) => {
    const newDate = setYear(displayMonth, year);
    setDisplayMonth(newDate);
    setYearPickerOpen(false);
  };

  return (
    <div className="relative">
      <DayPicker
        showOutsideDays={showOutsideDays}
        fixedWeeks
        month={displayMonth}
        onMonthChange={setDisplayMonth}
        className={cn("p-4", className)}
        classNames={{
          months: "flex flex-col",
          month: "flex flex-col gap-3",
          month_caption: "flex justify-center relative items-center h-10 mb-1",
          caption_label: "hidden", // Hide default label, we use custom
          nav: "flex items-center",
          button_previous: cn(
            "absolute left-3 h-9 w-9 translate-y-3.5",
            "inline-flex items-center justify-center rounded-full bg-white",
            "text-gray-600 hover:text-gray-900 hover:bg-gray-50",
            "border border-gray-300 shadow-sm",
            "transition-all focus:outline-none"
          ),
          button_next: cn(
            "absolute right-3 h-9 w-9 translate-y-3.5",
            "inline-flex items-center justify-center rounded-full bg-white",
            "text-gray-600 hover:text-gray-900 hover:bg-gray-50",
            "border border-gray-300 shadow-sm",
            "transition-all focus:outline-none"
          ),
          month_grid: "w-full border-collapse",
          weekdays: "flex",
          weekday:
            "text-gray-400 w-10 h-9 font-medium text-xs flex items-center justify-center",
          week: "flex w-full",
          day: cn(
            "relative p-0 text-center text-sm",
            "h-10 w-10"
          ),
          day_button: cn(
            "h-10 w-10 p-0 font-normal rounded-lg transition-all text-gray-700",
            "hover:bg-gray-100 hover:text-gray-900",
            "focus:outline-none focus:ring-2 focus:ring-brand-primary/30",
            "flex items-center justify-center cursor-pointer"
          ),
          range_end: "day-range-end",
          selected: cn(
            "[&>button]:bg-brand-primary [&>button]:text-white [&>button]:hover:bg-brand-primary [&>button]:hover:text-white",
            "[&>button]:font-semibold [&>button]:shadow-sm"
          ),
          today: "[&>button]:bg-indigo-50 [&>button]:text-brand-primary [&>button]:font-semibold",
          outside: "[&>button]:text-gray-300 [&>button]:hover:text-gray-400",
          disabled: "[&>button]:text-gray-300 [&>button]:cursor-not-allowed [&>button]:hover:bg-transparent",
          range_middle:
            "aria-selected:bg-brand-primary/10 aria-selected:text-brand-primary",
          hidden: "invisible",
          ...classNames,
        }}
        components={{
          Chevron: ({ orientation }) => {
            if (orientation === "left") {
              return <ChevronLeft className="h-4 w-4" />;
            }
            return <ChevronRight className="h-4 w-4" />;
          },
          MonthCaption: ({ calendarMonth }) => {
            const month = calendarMonth.date.getMonth();
            const year = calendarMonth.date.getFullYear();
            
            return (
              <div className="flex items-center justify-center gap-1">
                {/* Month selector */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { setMonthPickerOpen(!monthPickerOpen); setYearPickerOpen(false); }}
                    className="flex items-center gap-1 px-2 py-1 text-sm font-semibold text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {MONTHS[month]}
                    <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                  </button>
                  
                  {monthPickerOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setMonthPickerOpen(false)} />
                      <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-2 grid grid-cols-3 gap-1 w-[200px]">
                        {MONTHS.map((m, i) => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => handleMonthSelect(i)}
                            className={cn(
                              "px-2 py-1.5 text-sm rounded-lg transition-colors",
                              month === i
                                ? "bg-brand-primary text-white font-medium"
                                : "text-gray-700 hover:bg-gray-100"
                            )}
                          >
                            {m.slice(0, 3)}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                
                {/* Year selector */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { setYearPickerOpen(!yearPickerOpen); setMonthPickerOpen(false); }}
                    className="flex items-center gap-1 px-2 py-1 text-sm font-semibold text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {year}
                    <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                  </button>
                  
                  {yearPickerOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setYearPickerOpen(false)} />
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-2 max-h-[200px] overflow-y-auto w-[100px]">
                        {years.map((y) => (
                          <button
                            key={y}
                            type="button"
                            onClick={() => handleYearSelect(y)}
                            className={cn(
                              "w-full px-3 py-1.5 text-sm rounded-lg transition-colors text-center",
                              year === y
                                ? "bg-brand-primary text-white font-medium"
                                : "text-gray-700 hover:bg-gray-100"
                            )}
                          >
                            {y}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          },
        }}
        {...props}
      />
    </div>
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
